// Shared Lara chat for one quote (history saved on the quote, visible to all admins). Streams replies.
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { Send, Loader2, Sparkles, Trash2 } from "lucide-react";
import api, { API } from "../../lib/api";
import VoiceNoteButton from "../../pages/quotes/panels/VoiceNoteButton";
import { useAuth } from "../../context/AuthContext";

const STARTERS = [
  "How should I open the call?",
  "What's missing from this order?",
  "Explain the building regs simply",
  "Role-play the customer pushing on price",
  "What do they need for the next phase?",
];

export default function LaraChat({ quoteId, initial = [] }) {
  const { user } = useAuth() || {};
  const [msgs, setMsgs] = useState(initial);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { setMsgs(initial); }, [initial]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs]);

  const send = async (msg) => {
    const m = (msg ?? text).trim();
    if (!m || busy) return;
    setText(""); setBusy(true);
    const tmp = `tmp-${Date.now()}`;
    setMsgs((x) => [...x, { id: `u-${tmp}`, role: "user", content: m, by: user?.name || "You" }, { id: tmp, role: "assistant", content: "", by: "Lara" }]);
    try {
      const res = await fetch(`${API}/lara/quote/${quoteId}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("talmia_token")}` },
        body: JSON.stringify({ message: m }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Lara is unavailable"); }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        parts.forEach((p) => {
          const line = p.replace(/^data: /, "");
          try {
            const ev = JSON.parse(line);
            if (ev.delta) setMsgs((x) => x.map((mm) => (mm.id === tmp ? { ...mm, content: mm.content + ev.delta } : mm)));
            if (ev.error) toast.error(`Lara: ${ev.error}`);
            if (ev.done) setMsgs((x) => x.map((mm) => (mm.id === tmp ? ev.message : mm)));
          } catch { /* partial */ }
        });
      }
    } catch (e) {
      toast.error(e.message);
      setMsgs((x) => x.filter((mm) => mm.id !== tmp && mm.id !== `u-${tmp}`));
      setText(m);
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear this quote's Lara chat for everyone?")) return;
    await api.delete(`/lara/quote/${quoteId}/chat`); setMsgs([]);
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="lara-chat">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/[0.06]">
        <div className="text-[10.5px] uppercase tracking-widest font-mono text-slate-500">Ask Lara · shared with your team</div>
        {msgs.length > 0 && <button type="button" onClick={clear} className="text-slate-500 hover:text-rose-300" title="Clear chat" data-testid="lara-chat-clear"><Trash2 className="w-3.5 h-3.5" /></button>}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3" data-testid="lara-chat-messages">
        {msgs.length === 0 && (
          <div className="text-[12.5px] text-slate-400 space-y-2">
            <div className="flex items-center gap-1.5 text-violet-200"><Sparkles className="w-4 h-4" /> Ask anything about this quote, the build or how to win it.</div>
            <div className="flex flex-wrap gap-1.5">
              {STARTERS.map((s, i) => <button key={s} type="button" onClick={() => send(s)} className="px-2.5 py-1 rounded-full border border-violet-400/30 text-violet-200 text-[11.5px] hover:bg-violet-500/10" data-testid={`lara-starter-${i}`}>{s}</button>)}
            </div>
          </div>
        )}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`lara-msg-${m.role}`}>
            <div className={`max-w-[90%] rounded-xl px-3 py-2 text-[12.5px] leading-relaxed ${m.role === "user" ? "bg-orange-500/15 border border-orange-400/30 text-slate-100" : "bg-white/[0.04] border border-white/10 text-slate-200"}`}>
              <div className="text-[10px] font-mono text-slate-500 mb-0.5">{m.role === "user" ? m.by : "Lara"}</div>
              {m.role === "assistant"
                ? (m.content ? <div className="lara-md"><ReactMarkdown>{m.content}</ReactMarkdown></div> : <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-300" />)
                : <div className="whitespace-pre-wrap">{m.content}</div>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="border-t border-white/[0.06] p-2.5 flex items-end gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Ask Lara…" data-testid="lara-chat-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="flex-1 min-w-0 resize-none rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-[13px] text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-400/60" />
        <div className="flex flex-col gap-1.5">
          <VoiceNoteButton url="/ai/transcribe" testid="lara-chat-voice" label="Speak" onResult={({ raw, clean }) => setText((t) => [t, clean || raw].filter(Boolean).join(" "))} />
          <button type="button" onClick={() => send()} disabled={busy || !text.trim()} data-testid="lara-chat-send"
            className="h-[30px] px-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-[12px] font-semibold inline-flex items-center justify-center gap-1">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
          </button>
        </div>
      </div>
    </div>
  );
}
